import { useCallback, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";

export interface UsePendingEditsGuardOptions {
  /**
   * Whether the User Information form currently differs from the stored record.
   *
   * Passed in rather than computed here, because dirtiness is a comparison between
   * `useUserInfoEdits`'s overrides and the fetched summary and this hook owns neither.
   */
  isDirty: boolean;
  /**
   * Whether a save of those edits is in flight. A navigation is still held, but not prompted for:
   * the edits are being committed, not discarded. It goes through once the save lands (the form is
   * then clean), and prompts only if the save fails.
   */
  isSaving?: boolean;
  /** Discards the pending edits. Called only on an explicit choice to discard. */
  onDiscard: () => void;
}

export interface PendingEditsGuard {
  /** Whether the prompt is open. */
  isPromptOpen: boolean;
  /**
   * Run `action` — immediately when the form is clean, or after the operator confirms a discard
   * when it is dirty.
   *
   * Use this for **anything that would lose edits**: changing the selected user (FR-023) is the one
   * case today. **Do not** route filter, sort or page changes through it — FR-017 requires those to
   * prompt for nothing.
   */
  requestAction: (action: () => void) => void;
  /** Discards the edits and performs the deferred action. */
  confirmDiscard: () => void;
  /** Closes the prompt and keeps both the edits and the current selection. */
  cancel: () => void;
}

/**
 * The two prompts that stand between a pending edit and losing it — FR-023 and FR-024.
 *
 * Shared so both pages use it (`my-info-write` spec):
 *
 * - **FR-023 — selecting a different user.** User Manager only: My Info's subject never changes, so
 *   it never calls `requestAction`.
 * - **FR-024 — leaving the page.** Both pages, through the blocker below.
 *
 * **The action is deferred, not performed-then-asked.** `requestAction` stashes the callback and
 * opens the prompt; the callback runs **only** from `confirmDiscard`. Firing first and asking after
 * would load the new subject and then ask about the old one's edits — which is the data loss the
 * requirement exists to prevent (T087).
 *
 * **A clean form never prompts.** That is FR-024's other half: reverting a field to its original
 * value clears its unsaved state, so a panel with no *net* changes must go straight through. It
 * falls out of `useUserInfoEdits` comparing overrides against the record rather than tracking
 * "touched" — typing a value back to what it was is not dirty.
 *
 * **FR-017 is honoured by omission.** Filter, sort and page changes do not call `requestAction` at
 * all, so they cannot prompt and cannot discard. That is deliberate: routing them through the guard
 * "for consistency" would break the requirement.
 */
export const usePendingEditsGuard = ({
  isDirty,
  isSaving = false,
  onDiscard
}: UsePendingEditsGuardOptions): PendingEditsGuard => {
  const [deferredAction, setDeferredAction] = useState<(() => void) | null>(
    null
  );

  /*
   * FR-024. `useBlocker` intercepts an in-app route change while the form is dirty.
   *
   * It covers navigation **within the SPA** — the nav bar, a link out of Configuration. It does
   * **not** cover a browser reload or closing the tab; that needs a `beforeunload` listener, which
   * browsers render as their own generic dialog and which cannot carry FR-076's modal semantics.
   * Not wired here deliberately: FR-024's own wording is about leaving the page within the app.
   *
   * ⚠️ **It MUST compare pathnames, not block every navigation — a bare `useBlocker(isDirty)`
   * breaks FR-022.** The tab strip is a navigation: `useUserInfoTabs` keeps the open tab in the
   * `?tab=` search param and switches with `setSearchParams(next, { replace: true })`. So while the
   * form was dirty, `useBlocker(isDirty)` intercepted **every tab switch** and raised the
   * unsaved-changes prompt — which FR-022 forbids in as many words: "Switching tabs MUST preserve
   * pending edits … MUST NOT discard them", and prompting to discard on a tab switch is the
   * opposite of that. Found by T084's FR-022 case, 2026-09-14.
   *
   * A pathname comparison separates the two requirements cleanly, because they differ in exactly
   * this respect:
   *
   * | Action | pathname | Blocked? |
   * |---|---|---|
   * | switch tab (`?tab=login`) | unchanged | **no** — FR-022 |
   * | leave Configuration | changed | **yes** — FR-024 |
   *
   * Do **not** "simplify" this back to `useBlocker(isDirty)`. FR-006 permits the location to encode
   * which tab is open (it forbids only encoding *which user* is selected), so the tab strip will
   * keep being a navigation and this will keep being load-bearing.
   */
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  );

  /*
   * ⚠️ **`proceed()` must happen at most once per blocked navigation, and enforcing that needs a
   * ref.** Without it, react-router throws
   * `Invalid blocker state transition: unblocked -> proceeding`, which its default `ErrorBoundary`
   * catches — so in the app, discarding in order to leave the page took the whole page down.
   *
   * The double call is not obvious from either call site, because they are in different ticks:
   *
   * 1. `confirmDiscard` runs `onDiscard()` (which clears the edits) and then calls `proceed()`.
   * 2. Clearing the edits makes `isDirty` false, which re-renders and runs the effect below — whose
   *    `blocker` is the object from the render that *scheduled* it, still reporting `"blocked"`.
   * 3. So the effect calls `proceed()` on a blocker that has already proceeded.
   *
   * Reading `blocker.state` cannot prevent it for that reason: the state is stale by construction.
   * The ref records the decision rather than re-deriving it, and resets when the blocker genuinely
   * leaves the blocked state, so the next navigation gets a fresh one.
   *
   * Found 2026-09-14 via the `Invalid blocker state transition` console error in
   * `UserManager.spec.tsx`'s "navigates once the operator discards" — **which passed**. The
   * assertion was about the settled location, and the navigation did complete; only the console
   * showed that react-router had raised and swallowed an error on the way.
   */
  const hasProceededRef = useRef(false);

  useEffect(() => {
    if (blocker.state !== "blocked") {
      hasProceededRef.current = false;
    }
  }, [blocker.state]);

  const proceedOnce = useCallback(() => {
    if (blocker.state === "blocked" && !hasProceededRef.current) {
      hasProceededRef.current = true;
      blocker.proceed();
    }
  }, [blocker]);

  useEffect(() => {
    if (blocker.state === "blocked" && !isDirty) {
      // The form went clean while blocked (a revert, or a discard elsewhere) — let it through.
      proceedOnce();
    }
  }, [blocker.state, isDirty, proceedOnce]);

  const requestAction = useCallback(
    (action: () => void) => {
      if (!isDirty) {
        action();

        return;
      }

      /*
       * Stored in a closure because `useState`'s setter treats a bare function as an updater. A
       * function stashed as `setDeferredAction(action)` would be *called* with the previous state
       * instead of stored, so the action would fire immediately — the exact bug this hook exists to
       * prevent, and an easy one to reintroduce.
       */
      setDeferredAction(() => action);
    },
    [isDirty]
  );

  const confirmDiscard = useCallback(() => {
    onDiscard();
    deferredAction?.();
    setDeferredAction(null);
    proceedOnce();
  }, [deferredAction, onDiscard, proceedOnce]);

  const cancel = useCallback(() => {
    setDeferredAction(null);

    if (blocker.state === "blocked") {
      blocker.reset();
    }
  }, [blocker]);

  return {
    isPromptOpen:
      deferredAction !== null || (blocker.state === "blocked" && !isSaving),
    requestAction,
    confirmDiscard,
    cancel
  };
};
